// Pretty-prints an RQL equation for display/editing. Structure comes purely from parens/commas —
// a "call" is just an identifier immediately followed by '(', so this doesn't need to know the
// RQL_OPERATORS list at all. Whitespace introduced here is cosmetic only: equationParser.ts's
// tokenizer trims every token at its '('/','/')' boundary, so a formatted equation parses/
// executes identically to its flat form.

const INDENT_SIZE = 4;
const MAX_WIDTH = 80;

type Node =
  | { type: "call"; name: string; args: Node[] }
  | { type: "atom"; text: string };

class EquationFormatParseError extends Error {}

const isWordDelimiter = (ch: string | undefined): boolean =>
  ch === undefined || ch === "(" || ch === ")" || ch === "," || /\s/.test(ch);

const parseNode = (input: string, pos: { i: number }): Node => {
  const n = input.length;
  const skipWs = () => {
    while (pos.i < n && /\s/.test(input[pos.i])) pos.i++;
  };

  skipWs();

  if (input[pos.i] === '"' || input[pos.i] === "'") {
    const quote = input[pos.i];
    let j = pos.i + 1;
    while (j < n && input[j] !== quote) j++;
    if (j >= n) {
      throw new EquationFormatParseError("Unterminated quoted string.");
    }
    j++;
    const text = input.slice(pos.i, j);
    pos.i = j;
    return { type: "atom", text };
  }

  const start = pos.i;
  while (pos.i < n && !isWordDelimiter(input[pos.i])) pos.i++;
  const word = input.slice(start, pos.i);
  if (!word) {
    throw new EquationFormatParseError("Expected a value.");
  }

  skipWs();
  if (input[pos.i] !== "(") {
    return { type: "atom", text: word };
  }

  pos.i++; // consume '('
  const args: Node[] = [];
  skipWs();
  if (input[pos.i] !== ")") {
    args.push(parseNode(input, pos));
    skipWs();
    while (input[pos.i] === ",") {
      pos.i++;
      args.push(parseNode(input, pos));
      skipWs();
    }
  }
  skipWs();
  if (input[pos.i] !== ")") {
    throw new EquationFormatParseError(`Expected ')' to close '${word}('.`);
  }
  pos.i++; // consume ')'
  return { type: "call", name: word, args };
};

const renderFlat = (node: Node): string =>
  node.type === "atom"
    ? node.text
    : `${node.name}(${node.args.map(renderFlat).join(", ")})`;

// Renders `node` at `indentLevel`: flat if it fits within MAX_WIDTH at that indent, otherwise
// broken into one argument per line, each re-checked against the same rule at indentLevel + 1 —
// so nesting alone never forces a break, only actual line length does.
const renderNode = (node: Node, indentLevel: number): string => {
  if (node.type === "atom") {
    return node.text;
  }
  const indent = " ".repeat(INDENT_SIZE * indentLevel);
  const flat = renderFlat(node);
  if (indent.length + flat.length <= MAX_WIDTH) {
    return flat;
  }
  const childIndent = " ".repeat(INDENT_SIZE * (indentLevel + 1));
  const argLines = node.args.map(
    (arg) => `${childIndent}${renderNode(arg, indentLevel + 1)}`,
  );
  return `${node.name}(\n${argLines.join(",\n")}\n${indent})`;
};

// Returns the pretty-printed equation, or the original string unchanged if it doesn't parse as
// exactly one well-formed call (mismatched parens, trailing garbage, empty/malformed content) —
// formatting never partially rewrites a broken equation.
export const formatEquation = (equation: string): string => {
  const trimmed = equation.trim();
  if (!trimmed) {
    return equation;
  }
  try {
    const pos = { i: 0 };
    const root = parseNode(trimmed, pos);
    while (pos.i < trimmed.length && /\s/.test(trimmed[pos.i])) pos.i++;
    if (pos.i !== trimmed.length || root.type !== "call") {
      return equation;
    }
    return renderNode(root, 0);
  } catch {
    return equation;
  }
};
